/**
 * ABTestCard — Reusable Admin → System card that displays results of one
 * email subject-line A/B test. Used for both Day 11 (in-trial) and
 * Day 15 trial-expired emails.
 *
 * Props:
 *   adminToken   — auth bearer
 *   endpoint     — backend route under /api/admin/ab, e.g. "day11" or "expired"
 *   title        — heading text shown in the card
 *   description  — small text under the heading
 *   testid       — root data-testid attribute (also prefixed onto inner test ids)
 *   accentColor  — Tailwind color name used for the card chrome ("purple", "amber", etc.)
 */
import React, { useEffect, useState } from 'react';
import { FlaskConical, Loader2, RefreshCw, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Tailwind doesn't honor template-literal class names, so map them explicitly.
const COLORS = {
  purple: { from: 'from-purple-50', to: 'to-fuchsia-50', border: 'border-purple-200',
            icon: 'bg-purple-600', text: 'text-purple-900', sub: 'text-purple-800',
            btnBorder: 'border-purple-300', btnText: 'text-purple-900' },
  amber:  { from: 'from-amber-50',  to: 'to-orange-50',  border: 'border-amber-200',
            icon: 'bg-amber-600',  text: 'text-amber-900',  sub: 'text-amber-800',
            btnBorder: 'border-amber-300',  btnText: 'text-amber-900' },
};

export default function ABTestCard({
  adminToken, endpoint, title, description, testid = 'ab-card',
  accentColor = 'purple',
}) {
  const c = COLORS[accentColor] || COLORS.purple;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const res = await fetch(`${API}/api/admin/ab/${endpoint}`,
        { headers: { Authorization: `Bearer ${adminToken}` } });
      if (!res.ok) throw new Error(`Load failed: ${res.status}`);
      setData(await res.json());
    } catch (e) { setErr(e.message); }
    finally     { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [endpoint]);

  const variants = data?.variants || {};
  const winner = data?.winner;
  const lift   = data?.winnerLift;

  return (
    <div data-testid={testid}
         className={`bg-gradient-to-br ${c.from} ${c.to} border-2 ${c.border} rounded-xl p-5 space-y-4`}>
      <div className="flex items-start gap-3">
        <div className={`${c.icon} text-white rounded-lg p-2.5`}>
          <FlaskConical className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className={`text-base font-extrabold ${c.text}`}>{title}</h3>
          {description && <p className={`text-xs ${c.sub} mt-0.5 leading-snug`}>{description}</p>}
        </div>
        <button onClick={load} disabled={loading}
                className={`bg-white border-2 ${c.btnBorder} ${c.btnText} text-sm font-bold px-3 py-2 rounded inline-flex items-center gap-1 disabled:opacity-50`}
                data-testid={`${testid}-refresh`}>
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {err && (
        <div className="bg-rose-50 border border-rose-300 text-rose-900 text-sm rounded p-3"
             data-testid={`${testid}-error`}>{err}</div>
      )}

      {loading && !data && (
        <div className={`flex items-center gap-2 ${c.text} text-sm`} data-testid={`${testid}-loading`}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading test results…
        </div>
      )}

      {data && (
        <>
          {winner && (
            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-3 text-sm font-bold text-emerald-900 inline-flex items-center gap-2"
                 data-testid={`${testid}-winner`}>
              <TrendingUp className="w-4 h-4" />
              Variant {winner} is winning
              {lift && <span className="text-xs font-semibold text-emerald-700">(+{lift}% lift)</span>}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {['A', 'B'].map(v => {
              const x = variants[v] || {};
              const rate = ((x.conversionRate || 0) * 100).toFixed(1);
              const isWinner = winner === v;
              return (
                <div key={v}
                     className={`bg-white border-2 rounded-lg p-4 ${isWinner ? 'border-emerald-400 ring-2 ring-emerald-100' : c.border}`}
                     data-testid={`${testid}-variant-${v.toLowerCase()}`}>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className={`text-xl font-extrabold ${isWinner ? 'text-emerald-700' : c.text}`}>
                      Variant {v}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                      {x.label || ''}
                    </span>
                  </div>
                  <p className="text-xs text-slate-700 italic mb-3"
                     data-testid={`${testid}-subject-${v.toLowerCase()}`}>
                    "{x.subject || '—'}"
                  </p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-2xl font-extrabold text-slate-900">{x.sent ?? 0}</p>
                      <p className="text-[10px] text-slate-500 uppercase">Sent</p>
                    </div>
                    <div>
                      <p className="text-2xl font-extrabold text-blue-700">{x.converted ?? 0}</p>
                      <p className="text-[10px] text-slate-500 uppercase">Subscribed</p>
                    </div>
                    <div>
                      <p className={`text-2xl font-extrabold ${isWinner ? 'text-emerald-700' : 'text-slate-900'}`}>
                        {rate}%
                      </p>
                      <p className="text-[10px] text-slate-500 uppercase">Conv. rate</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {!winner && (
            <p className={`text-xs ${c.sub} italic`} data-testid={`${testid}-need-more`}>
              Both variants need at least 20 sends each before a winner is declared.
              You currently have {variants.A?.sent ?? 0} (A) and {variants.B?.sent ?? 0} (B).
            </p>
          )}
        </>
      )}
    </div>
  );
}
